// Shared sanitizers for chatbot responses

/*
 * The chatbot sometimes streams structured payloads (items, cart snapshots,
 * UUIDs, etc.) that must never reach the UI. We detect those markers as soon
 * as possible and clip both the current chunk and any subsequent chunks from
 * the stream.
 */

const STREAMING_LOOKBEHIND = 120;

type SensitiveTrigger =
  | "itemsIdTag"
  | "cartTag"
  | "uuidFragment"
  | "jsonSignature"
  | "hexSequence";

interface SensitiveMatch {
  index: number;
  trigger: SensitiveTrigger;
}

const SENSITIVE_PATTERNS: Array<{ regex: RegExp; trigger: SensitiveTrigger }> = [
  { regex: /<ITEMS?_ID>/i, trigger: "itemsIdTag" },
  { regex: /<\/ITEMS?_ID>/i, trigger: "itemsIdTag" },
  { regex: /<cart>/i, trigger: "cartTag" },
  { regex: /<\/cart>/i, trigger: "cartTag" },
  {
    regex: /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i,
    trigger: "uuidFragment",
  },
  {
    regex: /["']?[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-$/i,
    trigger: "uuidFragment",
  },
  {
    regex: /-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}["']?/i,
    trigger: "uuidFragment",
  },
  {
    regex: /["{]\s*[a-z]+[A-Z][a-zA-Z]*"\s*:\s*["{\[]/i,
    trigger: "jsonSignature",
  },
  {
    regex: /["']?[a-f0-9]{3,8}(?:-[a-f0-9]{2,12}){2,}["']?/i,
    trigger: "hexSequence",
  },
];

const SUPPRESSION_TRIGGERS = new Set<SensitiveTrigger>([
  "itemsIdTag",
  "cartTag",
  "uuidFragment",
  "jsonSignature",
  "hexSequence",
]);

let suppressStreamingAfterItems = false;
let streamingTail = "";

const findSensitiveDataCutoff = (value: string): SensitiveMatch | null => {
  if (!value) {
    return null;
  }

  let earliest: SensitiveMatch | null = null;

  for (const { regex, trigger } of SENSITIVE_PATTERNS) {
    const match = value.match(regex);
    if (!match || match.index === undefined) {
      continue;
    }

    if (!earliest || match.index < earliest.index) {
      earliest = { index: match.index, trigger };
    }
  }

  return earliest;
};

const collapseWhitespace = (value: string): string => {
  return value
    .replace(/\r\n/g, "\n")
    .replace(/[\t\v\f]+/g, " ")
    .replace(/ {2,}/g, " ")
    .replace(/\s+\n/g, "\n")
    .replace(/\n\s+/g, "\n")
    .replace(/\n{3,}/g, "\n\n");
};

export const resetStreamingSanitizerState = (): void => {
  suppressStreamingAfterItems = false;
  streamingTail = "";
};

export const stripCartDataFromResponse = (value: string): string => {
  if (!value) {
    return "";
  }

  let result = String(value);

  const fieldPatterns = [
    /"currencyName"\s*:\s*"[^"]*"[\s,]*/gi,
    /"currencyId"\s*:\s*"[^"]*"[\s,]*/gi,
    /"totalQuantity"\s*:\s*\d+[\s,]*/gi,
    /"total"\s*:\s*[\d.]+[\s,]*/gi,
    /"lineItemId"\s*:\s*"[^"]*"[\s,]*/gi,
    /"productId"\s*:\s*"[^"]*"[\s,]*/gi,
    /"quantity"\s*:\s*\d+[\s,]*/gi,
    /"basePrice"\s*:\s*[\d.]+[\s,]*/gi,
    /"item_notes"\s*:\s*"[^"]*"[\s,]*/gi,
    /"order_notes"\s*:\s*"[^"]*"[\s,]*/gi,
    /"items"\s*:\s*\[[\s\S]*?\][\s,]*/gi,
    /"options"\s*:\s*\[[\s\S]*?\][\s,]*/gi,
    /"selections"\s*:\s*\[[\s\S]*?\][\s,]*/gi,
  ];

  for (const pattern of fieldPatterns) {
    result = result.replace(pattern, "");
  }

  result = result.replace(
    /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi,
    ""
  );

  result = result.replace(
    /\{[^}]*(?:currencyName|totalQuantity|lineItemId|productId)[^}]*\}/gi,
    ""
  );
  result = result.replace(
    /\[[^\]]*\{[^}]*?(?:lineItemId|productId)[^}]*\}[^\]]*\]/gi,
    ""
  );

  result = result.replace(
    /([.!?]\s*)(?:\W*\w+)?\s*"?-?[a-f0-9]{8,}"?,?\s*"currencyName"[\s\S]*/gi,
    "$1"
  );

  const cartTriggers = [
    /"currencyName"\s*:/i,
    /"totalQuantity"\s*:/i,
    /"items"\s*:/i,
    /"lineItemId"\s*:/i,
    /"productId"\s*:/i,
  ];

  for (const trigger of cartTriggers) {
    const match = result.match(trigger);
    if (match && match.index !== undefined) {
      result = result.slice(0, match.index);
      break;
    }
  }

  result = result.replace(/[\{\}\[\]]+/g, "");
  result = result.replace(/\s+,\s+/g, ", ");
  result = collapseWhitespace(result);

  return result;
};

export const stripFromUncloseItemsId = (value: string): string => {
  if (!value) {
    return "";
  }

  const match = findSensitiveDataCutoff(value);
  if (!match) {
    return value;
  }

  return value.slice(0, match.index);
};

export const stripChatbotMarkup = (value: string): string => {
  if (!value) {
    return "";
  }

  let result = stripFromUncloseItemsId(String(value));

  result = result
    .replace(/<([A-Z0-9_-]+)>[\s\S]*?<\/\1>/gi, "")
    .replace(/<\/[A-Z0-9_-]+\s*>/gi, "")
    .replace(/<[A-Z0-9_-]+\s*\/>/gi, "")
    .replace(/<([A-Z0-9_-]+)>[\s\S]*$/gi, "");

  const lastOpen = result.lastIndexOf("<");
  const lastClose = result.lastIndexOf(">");
  if (lastOpen > lastClose && lastOpen !== -1) {
    result = result.substring(0, lastOpen);
  }

  result = stripCartDataFromResponse(result);

  return result;
};

const computeStreamingOverlap = (previous: string, nextValue: string): number => {
  if (!previous || !nextValue) {
    return 0;
  }

  const max = Math.min(previous.length, nextValue.length);
  for (let length = max; length > 0; length -= 1) {
    if (previous.slice(-length) === nextValue.slice(0, length)) {
      return length;
    }
  }

  return 0;
};

export const prepareStreamingChunk = (
  chunk: string,
  previousSanitizedText = ""
): string => {
  if (!chunk) {
    return "";
  }

  if (suppressStreamingAfterItems) {
    return "";
  }

  const windowSource = `${streamingTail}${chunk}`;
  const match = findSensitiveDataCutoff(windowSource);
  let processedChunk = chunk;
  let suppressAfterChunk = false;

  if (match) {
    const relativeIndex = match.index - streamingTail.length;
    if (relativeIndex <= 0) {
      processedChunk = "";
    } else if (relativeIndex < processedChunk.length) {
      processedChunk = processedChunk.slice(0, relativeIndex);
    }

    if (SUPPRESSION_TRIGGERS.has(match.trigger)) {
      suppressAfterChunk = true;
    }
  }

  streamingTail = windowSource.slice(-STREAMING_LOOKBEHIND);

  if (!processedChunk || suppressStreamingAfterItems) {
    if (suppressAfterChunk) {
      suppressStreamingAfterItems = true;
    }
    return "";
  }

  let sanitized = stripChatbotMarkup(processedChunk);
  if (!sanitized) {
    if (suppressAfterChunk) {
      suppressStreamingAfterItems = true;
    }
    return "";
  }

  sanitized = collapseWhitespace(sanitized);

  const overlap = computeStreamingOverlap(previousSanitizedText, sanitized);
  if (overlap > 0) {
    sanitized = sanitized.slice(overlap);
  }

  if (!sanitized) {
    return "";
  }

  if (
    previousSanitizedText &&
    sanitized &&
    !/\s/.test(previousSanitizedText.slice(-1)) &&
    !/\s/.test(sanitized.charAt(0))
  ) {
    sanitized = ` ${sanitized}`;
  }

  if (suppressAfterChunk) {
    suppressStreamingAfterItems = true;
  }

  return sanitized;
};

export const normalizeChatbotResponse = (value: string): string => {
  if (!value) {
    return "";
  }

  const withoutMarkup = stripChatbotMarkup(value);
  if (!withoutMarkup) {
    return "";
  }

  const match = findSensitiveDataCutoff(withoutMarkup);
  const truncated = match ? withoutMarkup.slice(0, match.index) : withoutMarkup;

  return collapseWhitespace(truncated).trim();
};